import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader2, ArrowUpRight, ArrowDownRight, Minus, Radio, AlertCircle, Zap, LineChart } from "lucide-react";
import TopBar from "@/components/TopBar";
import TickerSearchDialog from "@/components/TickerSearchDialog";
import DisclaimerFooter from "@/components/DisclaimerFooter";
import DataQualityWarning from "@/components/DataQualityWarning";
import CandlestickChart, { EMA_COLORS } from "@/components/CandlestickChart";
import IndicatorPanel from "@/components/IndicatorPanel";
import SignalCard from "@/components/SignalCard";
import ScoreBreakdown from "@/components/ScoreBreakdown";
import ContextRow from "@/components/ContextRow";
import SimilarSetups from "@/components/SimilarSetups";
import AIAnalystPanel from "@/components/AIAnalystPanel";
import FreshnessBadge from "@/components/FreshnessBadge";
import IntradaySignalCard from "@/components/IntradaySignalCard";
import { pushRecent } from "@/components/TickerSearchDialog";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTradingStyle } from "@/contexts/TradingStyleContext";

const SWING_TIMEFRAMES = ["1D", "4H", "1H"];
const INTRADAY_TIMEFRAMES = ["1m", "5m", "15m", "1h"];
const INTRADAY_POLL_MS = 20_000;

const OVERLAY_KEYS = [
  { key: "ema9", label: "EMA 9" },
  { key: "ema20", label: "EMA 20" },
  { key: "ema50", label: "EMA 50" },
  { key: "ema200", label: "EMA 200" },
  { key: "vwap", label: "VWAP" },
];

function fmtPrice(v) {
  if (v == null) return "—";
  const n = Number(v);
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function StockDetail() {
  const { ticker: raw } = useParams();
  const ticker = (raw || "").toUpperCase();
  const { style } = useTradingStyle();
  const isIntraday = style === "INTRADAY";
  const TIMEFRAMES = isIntraday ? INTRADAY_TIMEFRAMES : SWING_TIMEFRAMES;
  const defaultTf = isIntraday ? "5m" : "1D";
  const [openSearch, setOpenSearch] = useState(false);

  const [company, setCompany] = useState(null);
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");

  // tf may lag behind style for one render; compute the effective tf during render
  // so we never fire the wrong endpoint with the previous mode's timeframe.
  const [tf, setTf] = useState(defaultTf);
  const effectiveTf = TIMEFRAMES.includes(tf) ? tf : defaultTf;
  const [barsData, setBarsData] = useState(null);
  const [barsLoading, setBarsLoading] = useState(false);

  const [analysis, setAnalysis] = useState(null);
  const [analysisLoading, setAnalysisLoading] = useState(false);

  const [signalResult, setSignalResult] = useState(null);
  const [signalLoading, setSignalLoading] = useState(false);
  const [intradaySignal, setIntradaySignal] = useState(null);
  const [intradaySignalLoading, setIntradaySignalLoading] = useState(false);

  // Reset timeframe when trading style changes (updates state; effective tf above is what actually gets fetched)
  useEffect(() => { setTf(defaultTf); }, [style]); // eslint-disable-line react-hooks/exhaustive-deps

  const [overlaysVisible, setOverlaysVisible] = useState({
    ema9: false, ema20: true, ema50: true, ema200: true, vwap: false,
  });

  // Command palette shortcut
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpenSearch(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Fetch company + quote once per ticker; poll quote in intraday mode
  useEffect(() => {
    let cancelled = false;
    let pollId;
    if (!ticker) return;
    setError("");
    setCompany(null);
    setQuote(null);
    const fetchCompany = () => api.get(`/market/company/${ticker}`);
    const fetchQuote = () =>
      isIntraday
        ? api.get(`/intraday/quote/${ticker}`)
        : api.get(`/market/quote/${ticker}`);

    Promise.allSettled([fetchCompany(), fetchQuote()]).then(([c, q]) => {
      if (cancelled) return;
      if (c.status === "fulfilled") {
        setCompany(c.value.data);
        pushRecent({ ticker, name: c.value.data?.name, exchange: c.value.data?.exchange });
      } else {
        const detail = c.reason?.response?.data?.detail;
        setError(formatApiErrorDetail(detail) || `Ticker ${ticker} not found`);
      }
      if (q.status === "fulfilled") setQuote(q.value.data);
    });

    if (isIntraday) {
      pollId = setInterval(() => {
        fetchQuote()
          .then((r) => { if (!cancelled) setQuote(r.data); })
          .catch(() => {});
      }, INTRADAY_POLL_MS);
    }
    return () => { cancelled = true; if (pollId) clearInterval(pollId); };
  }, [ticker, isIntraday]);

  // Fetch bars for selected timeframe (poll in intraday mode)
  useEffect(() => {
    let cancelled = false;
    let pollId;
    if (!ticker) return;
    setBarsLoading(true);
    setBarsData(null);
    const path = isIntraday ? `/intraday/bars/${ticker}` : `/market/bars/${ticker}`;
    const params = { timeframe: effectiveTf, include_overlays: true };
    const load = (silent = false) => {
      if (!silent) setBarsLoading(true);
      return api.get(path, { params })
        .then((r) => { if (!cancelled) setBarsData(r.data); })
        .catch(() => { if (!cancelled && !silent) setBarsData(null); })
        .finally(() => { if (!cancelled && !silent) setBarsLoading(false); });
    };
    load(false);
    if (isIntraday) {
      pollId = setInterval(() => load(true), INTRADAY_POLL_MS);
    }
    return () => { cancelled = true; if (pollId) clearInterval(pollId); };
  }, [ticker, effectiveTf, isIntraday]);

  // Fetch analysis (daily-based) once per ticker — swing only
  useEffect(() => {
    let cancelled = false;
    if (!ticker || isIntraday) { setAnalysis(null); return; }
    setAnalysisLoading(true);
    setAnalysis(null);
    api.get(`/market/analysis/${ticker}`)
      .then((r) => { if (!cancelled) setAnalysis(r.data); })
      .catch(() => { if (!cancelled) setAnalysis(null); })
      .finally(() => { if (!cancelled) setAnalysisLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, isIntraday]);

  // Fetch signal engine result once per ticker — swing only (I2 will add intraday signals)
  useEffect(() => {
    let cancelled = false;
    if (!ticker || isIntraday) { setSignalResult(null); return; }
    setSignalLoading(true);
    setSignalResult(null);
    api.get(`/signal/analyze/${ticker}`)
      .then((r) => { if (!cancelled) setSignalResult(r.data); })
      .catch(() => { if (!cancelled) setSignalResult(null); })
      .finally(() => { if (!cancelled) setSignalLoading(false); });
    return () => { cancelled = true; };
  }, [ticker, isIntraday]);

  // Fetch INTRADAY signal — every 30s while in intraday mode
  useEffect(() => {
    let cancelled = false;
    let pollId;
    if (!ticker || !isIntraday) { setIntradaySignal(null); return; }
    const load = (silent = false) => {
      if (!silent) setIntradaySignalLoading(true);
      return api.get(`/intraday/signal/analyze/${ticker}`)
        .then((r) => { if (!cancelled) setIntradaySignal(r.data); })
        .catch(() => { if (!cancelled && !silent) setIntradaySignal(null); })
        .finally(() => { if (!cancelled && !silent) setIntradaySignalLoading(false); });
    };
    load(false);
    pollId = setInterval(() => load(true), 30_000);
    return () => { cancelled = true; if (pollId) clearInterval(pollId); };
  }, [ticker, isIntraday]);

  const dir = quote?.day_change ?? 0;
  const dirCls = dir > 0 ? "text-emerald-400" : dir < 0 ? "text-red-400" : "text-slate-400";
  const DirIcon = dir > 0 ? ArrowUpRight : dir < 0 ? ArrowDownRight : Minus;

  const warnings = useMemo(() => {
    const w = [];
    if (analysis?.report?.warnings) w.push(...analysis.report.warnings);
    if (barsData && barsData.bars_total_available < 200 && effectiveTf === "1D") {
      w.push(`Only ${barsData.bars_total_available} daily bars available. Trend indicators may be less reliable.`);
    }
    if (!isIntraday && quote?.delayed) {
      w.push("Quotes and bars are shown as reported by the current data provider.");
    }
    if (isIntraday && quote?.feed === "iex") {
      w.push("Alpaca SIP subscription NOT active on this account — showing IEX feed (partial tape). Contact billing to enable SIP.");
    }
    if (isIntraday && barsData && !barsData.market_is_open) {
      const sessLabel = barsData.session === "PRE_MARKET"
        ? "pre-market" : barsData.session === "AFTER_HOURS"
        ? "after-hours" : "closed";
      w.push(`Market is ${sessLabel}. Intraday bars reflect the last completed session; live updates resume at next open.`);
    }
    return w;
  }, [analysis, barsData, quote, effectiveTf, isIntraday]);

  // Price lines (entry, stop, targets) for the current chart, only on 1D timeframe.
  const chartPriceLines = useMemo(() => {
    if (isIntraday) return [];
    if (!signalResult || !signalResult.entry_plan) return [];
    if (effectiveTf !== "1D") return []; // only show on daily
    const p = signalResult.entry_plan;
    const isLong = signalResult.signal === "LONG";
    const targetColor = isLong ? "#10B981" : "#EF4444";
    const targetPrefix = isLong ? "T" : "T";
    const lines = [];
    if (p.ideal_entry != null)
      lines.push({ price: p.ideal_entry, color: "#06B6D4", title: "Ideal", lineStyle: 0 });
    if (p.entry_zone?.low != null)
      lines.push({ price: p.entry_zone.low, color: "#0891B2", title: "Zone Lo", lineStyle: 2 });
    if (p.entry_zone?.high != null)
      lines.push({ price: p.entry_zone.high, color: "#0891B2", title: "Zone Hi", lineStyle: 2 });
    if (p.stop_loss != null)
      lines.push({ price: p.stop_loss, color: "#EF4444", title: "Stop", lineStyle: 2 });
    if (p.targets?.t1 != null)
      lines.push({ price: p.targets.t1, color: targetColor, title: `${targetPrefix}1` });
    if (p.targets?.t2 != null)
      lines.push({ price: p.targets.t2, color: targetColor, title: `${targetPrefix}2` });
    if (p.targets?.t3 != null)
      lines.push({ price: p.targets.t3, color: targetColor, title: `${targetPrefix}3` });
    return lines;
  }, [signalResult, effectiveTf, isIntraday]);

  return (
    <div className="min-h-screen bg-[#080B11] flex flex-col">
      <TopBar onOpenSearch={() => setOpenSearch(true)} />
      <main className="flex-1 pb-14">
        <div className="max-w-[1400px] mx-auto p-3 sm:p-4">
          {/* Header banner */}
          <div className="border border-[#1E293B] bg-[#0F1626] px-4 py-3 flex flex-wrap items-center gap-x-6 gap-y-2">
            <div className="flex items-baseline gap-3">
              <span
                data-testid="stock-symbol-header"
                className="mono text-3xl font-extrabold tracking-tight text-slate-100"
              >
                {ticker}
              </span>
              <span
                data-testid="stock-company-name"
                className="text-slate-400 text-sm truncate max-w-[300px]"
              >
                {company?.name || (error ? "Unknown ticker" : "Loading…")}
              </span>
              {company?.exchange && (
                <span className="mono text-[10px] px-1.5 py-0.5 border border-slate-700 text-slate-500 uppercase tracking-widest">
                  {company.exchange}
                </span>
              )}
              {!error && !isIntraday && (
                <span className="mono text-[10px] px-1.5 py-0.5 border border-emerald-500/30 text-emerald-400 uppercase tracking-widest flex items-center gap-1" data-testid="stock-data-source-badge">
                  <Radio className="h-2.5 w-2.5" /> Real-time SIP
                </span>
              )}
              {!error && isIntraday && (
                <FreshnessBadge
                  freshness={quote?.freshness}
                  feed={quote?.feed || barsData?.feed}
                  session={quote?.session || barsData?.session}
                />
              )}
              {!error && (
                <span
                  data-testid="mode-badge"
                  className={`mono text-[10px] px-1.5 py-0.5 border uppercase tracking-widest flex items-center gap-1 ${
                    isIntraday
                      ? "border-amber-500/40 text-amber-300 bg-amber-500/10"
                      : "border-cyan-500/40 text-cyan-300 bg-cyan-500/10"
                  }`}
                >
                  {isIntraday ? <Zap className="h-2.5 w-2.5" /> : <LineChart className="h-2.5 w-2.5" />}
                  {isIntraday ? "Intraday" : "Swing"}
                </span>
              )}
            </div>
            <div className="ml-auto flex items-baseline gap-4">
              <div className="flex flex-col items-end">
                <span
                  data-testid="stock-price-display"
                  className="mono text-3xl font-extrabold tabular-nums text-slate-100"
                >
                  {error ? "—" : fmtPrice(quote?.price)}
                </span>
                <span className="mono text-[10px] uppercase tracking-widest text-slate-500">
                  Last price · USD
                </span>
              </div>
              <div className={`flex flex-col items-end mono tabular-nums ${error ? "text-slate-500" : dirCls}`}>
                <span data-testid="stock-day-change" className="text-lg font-bold flex items-center gap-1">
                  {!error && <DirIcon className="h-4 w-4" />}
                  {error ? "—" : quote ? (dir >= 0 ? "+" : "") + fmtPrice(dir) : "—"}
                </span>
                <span data-testid="stock-day-change-pct" className="text-sm">
                  {error ? "—" : quote ? (quote.day_change_pct >= 0 ? "+" : "") + Number(quote.day_change_pct).toFixed(2) + "%" : "—"}
                </span>
              </div>
            </div>
          </div>

          {error && (
            <div
              data-testid="ticker-error-banner"
              className="mt-3 border border-red-500/40 bg-red-500/10 px-3 py-3 mono text-xs text-red-200 flex items-center gap-2"
            >
              <AlertCircle className="h-4 w-4 text-red-400 shrink-0" />
              <span className="flex-1">{error}</span>
              <span className="text-red-400/60 text-[10px] uppercase tracking-widest">
                Use Cmd/Ctrl+K to search another ticker
              </span>
            </div>
          )}

          {!error && warnings.length > 0 && (
            <div className="mt-3">
              <DataQualityWarning warnings={warnings} />
            </div>
          )}

          {!error && (
          <>
          {/* Chart section */}
          <div className="mt-3 border border-[#1E293B] bg-[#0F1626]">
            {/* Toolbar */}
            <div className="flex flex-wrap items-center gap-3 border-b border-[#1E293B] px-3 py-2">
              <div className="flex items-center gap-1" data-testid="timeframe-switcher">
                {TIMEFRAMES.map((t) => (
                  <button
                    key={t}
                    data-testid={`timeframe-btn-${t}`}
                    onClick={() => setTf(t)}
                    className={`mono text-[11px] uppercase tracking-widest px-2.5 h-7 border transition-colors ${
                      effectiveTf === t
                        ? "border-cyan-500/60 bg-cyan-500/15 text-cyan-200"
                        : "border-[#1E293B] bg-[#0B111C] text-slate-400 hover:text-slate-200 hover:border-[#334155]"
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
              <div className="h-5 w-px bg-[#1E293B]" />
              <div className="flex flex-wrap items-center gap-1">
                {OVERLAY_KEYS.map((o) => {
                  const active = overlaysVisible[o.key];
                  return (
                    <button
                      key={o.key}
                      data-testid={`overlay-toggle-${o.key}`}
                      onClick={() =>
                        setOverlaysVisible((s) => ({ ...s, [o.key]: !s[o.key] }))
                      }
                      className={`flex items-center gap-1.5 mono text-[11px] uppercase tracking-widest px-2 h-7 border transition-colors ${
                        active
                          ? "text-slate-100 bg-[#172036]"
                          : "text-slate-500 bg-[#0B111C] hover:text-slate-300"
                      }`}
                      style={active ? { borderColor: EMA_COLORS[o.key] } : { borderColor: "#1E293B" }}
                    >
                      <span
                        className="inline-block w-2 h-2"
                        style={{ background: active ? EMA_COLORS[o.key] : "#334155" }}
                      />
                      {o.label}
                    </button>
                  );
                })}
              </div>
              <div className="ml-auto mono text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-3">
                {barsLoading && (
                  <span className="flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Loading…</span>
                )}
                {barsData && (
                  <span>
                    {barsData.bars?.length || 0} bars · Total {barsData.bars_total_available}
                  </span>
                )}
              </div>
            </div>

            {/* Chart */}
            <div className="relative">
              {(!barsData || !barsData.bars?.length) && !barsLoading && (
                <div className="absolute inset-0 flex items-center justify-center text-slate-500 mono text-xs">
                  {error ? "No data for this ticker" : "No bars available"}
                </div>
              )}
              <CandlestickChart
                bars={barsData?.bars || []}
                overlays={barsData?.overlays || {}}
                overlaysVisible={overlaysVisible}
                priceLines={chartPriceLines}
                height={520}
              />
            </div>
          </div>

          {/* Signal card — swing only. Intraday signals ship in Phase I2. */}
          {!isIntraday && signalLoading && (
            <div className="mt-4">
              <div className="border border-[#1E293B] bg-[#0F1626] p-6 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
                <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running signal engine…
              </div>
            </div>
          )}
          {!isIntraday && !signalLoading && signalResult && (
            <div className="mt-4">
              <SignalCard result={signalResult} />
            </div>
          )}

          {/* Intraday signal card (replaces the Phase I2 placeholder) */}
          {isIntraday && (
            <div className="mt-4" data-testid="intraday-signal-section">
              {intradaySignalLoading && !intradaySignal && (
                <div className="border border-[#1E293B] bg-[#0F1626] p-6 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Running intraday engine…
                </div>
              )}
              {intradaySignal && <IntradaySignalCard result={intradaySignal} />}
              {!intradaySignalLoading && !intradaySignal && (
                <div className="border border-[#1E293B] bg-[#0F1626] p-4 mono text-[11px] text-slate-500 text-center">
                  Intraday engine unavailable for this ticker.
                </div>
              )}
            </div>
          )}

          {/* Context row */}
          {!isIntraday && !signalLoading && signalResult && (
            <div className="mt-3">
              <ContextRow result={signalResult} />
            </div>
          )}

          {/* Score breakdown */}
          {!isIntraday && !signalLoading && signalResult && signalResult.contributions && (
            <div className="mt-3">
              <ScoreBreakdown result={signalResult} />
            </div>
          )}

          {/* Similar historical setups */}
          {!isIntraday && !signalLoading && signalResult && (
            <div className="mt-3">
              <SimilarSetups ticker={ticker} />
            </div>
          )}

          {/* AI Analyst */}
          {!isIntraday && !signalLoading && signalResult && signalResult.snapshot && (
            <div className="mt-3">
              <AIAnalystPanel ticker={ticker} />
            </div>
          )}

          {/* Indicator dashboard — swing only */}
          {!isIntraday && (
            <div className="mt-4">
              {analysisLoading && (
                <div className="border border-[#1E293B] bg-[#0F1626] p-6 text-slate-500 mono text-xs text-center flex items-center justify-center gap-2">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Computing indicator engine…
                </div>
              )}
              {!analysisLoading && analysis?.report && (
                <IndicatorPanel report={analysis.report} />
              )}
              {!analysisLoading && !analysis?.report && !error && (
                <div className="border border-[#1E293B] bg-[#0F1626] p-6 text-slate-500 mono text-xs text-center">
                  Indicator engine unavailable for this ticker.
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>
      </main>

      <TickerSearchDialog open={openSearch} onOpenChange={setOpenSearch} />
      <DisclaimerFooter />
    </div>
  );
}
